/**
 * Formas de pagamento da cobrança — tipos, rótulos e classificação.
 *
 * Centraliza tudo que é compartilhado entre a captura (PaymentPartsEditor,
 * usado no AppointmentDetailModal e no AppointmentFormModal) e o consumo
 * (relatório de recebimentos por forma de pagamento, BalanceModal).
 *
 * Regra do relatório:
 *   - 2+ formas numa cobrança  → bucket HÍBRIDO;
 *   - 1 forma                  → o bucket daquela forma (cartão de crédito
 *                                detalha nº de parcelas);
 *   - sem `paymentParts`       → tenta interpretar o `paymentMethod` legado
 *                                (texto livre); se não casar, SEM_FORMA.
 */

import type { Appointment, PaymentPart } from '@/types/models';

export type PaymentMethod = PaymentPart['method'];

/** Ordem canônica das formas no seletor da cobrança. */
export const PAYMENT_METHOD_ORDER: PaymentMethod[] = [
  'PIX',
  'DINHEIRO',
  'CARTAO_CREDITO',
  'CARTAO_DEBITO',
  'TRANSFERENCIA',
  'CONVENIO',
  'PACOTE',
  'OUTRO',
];
// 'ANTECIPADO' fica fora do seletor: "pago antes" é QUANDO, não COMO —
// vive em `Appointment.paymentTiming`. O tipo continua aceitando o valor
// pra ler os poucos registros gravados enquanto ele foi forma.

/** Rótulo completo (seletor, cabeçalho de grupo). */
export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  PIX: 'Pix',
  DINHEIRO: 'Dinheiro / espécie',
  CARTAO_CREDITO: 'Cartão de crédito',
  CARTAO_DEBITO: 'Cartão de débito',
  TRANSFERENCIA: 'Transferência bancária',
  ANTECIPADO: 'Pagamento antecipado',
  CONVENIO: 'Convênio',
  PACOTE: 'Pacote',
  OUTRO: 'Outro',
};

/** Rótulo curto (chips, células de tabela). */
export const PAYMENT_METHOD_SHORT: Record<PaymentMethod, string> = {
  PIX: 'Pix',
  DINHEIRO: 'Dinheiro',
  CARTAO_CREDITO: 'Crédito',
  CARTAO_DEBITO: 'Débito',
  TRANSFERENCIA: 'Transferência',
  ANTECIPADO: 'Antecipado',
  CONVENIO: 'Convênio',
  PACOTE: 'Pacote',
  OUTRO: 'Outro',
};

/**
 * Bucket de agrupamento do relatório: as formas + HÍBRIDO (várias formas
 * na mesma cobrança) + SEM_FORMA (cobrança sem forma informada).
 */
export type PaymentBucket = PaymentMethod | 'HIBRIDO' | 'SEM_FORMA';

/** Ordem dos grupos no relatório — espelha o pedido do médico. */
export const PAYMENT_BUCKET_ORDER: PaymentBucket[] = [
  'PIX',
  'DINHEIRO',
  'CARTAO_CREDITO',
  'CARTAO_DEBITO',
  'HIBRIDO',
  'TRANSFERENCIA',
  'ANTECIPADO',
  'CONVENIO',
  'PACOTE',
  'OUTRO',
  'SEM_FORMA',
];

export const PAYMENT_BUCKET_LABEL: Record<PaymentBucket, string> = {
  ...PAYMENT_METHOD_LABEL,
  HIBRIDO: 'Híbrido (várias formas)',
  SEM_FORMA: 'Sem forma informada',
};

/** Nº de parcelas suportadas no seletor de cartão de crédito (1 = à vista). */
export const MAX_INSTALLMENTS = 12;

/** "à vista" / "3×" — usado na composição e no detalhamento do relatório. */
export function installmentsLabel(n: number | undefined): string {
  return !n || n <= 1 ? 'à vista' : `${n}×`;
}

/**
 * Interpreta o `paymentMethod` legado (texto livre) numa forma canônica.
 * Tolerante a acentos/abreviações ("Cartão de débito", "credito", "espécie").
 * Retorna null pra texto vazio.
 */
export function parseLegacyMethod(text: string | undefined | null): PaymentMethod | null {
  if (!text) return null;
  const t = text.trim().toLowerCase();
  if (!t) return null;
  if (t.includes('pix')) return 'PIX';
  if (t.includes('espéc') || t.includes('espec') || t.includes('dinheiro')) return 'DINHEIRO';
  if (t.includes('transf')) return 'TRANSFERENCIA';
  if (t.includes('antecip')) return 'ANTECIPADO';
  if (t.includes('conv')) return 'CONVENIO';
  if (t.includes('pacote')) return 'PACOTE';
  if (t.includes('déb') || t.includes('deb')) return 'CARTAO_DEBITO';
  if (t.includes('créd') || t.includes('cred')) return 'CARTAO_CREDITO';
  if (t.includes('cart')) return 'CARTAO_CREDITO'; // "cartão" genérico → crédito
  return 'OUTRO';
}

/**
 * Rótulo curto de UMA forma (com parcelas quando crédito; com a observação
 * de onde está registrado quando antecipado — "Antecipado (fichário 3, pág.
 * 12)"). Como `compositionLabel`/`compositionBreakdown`/
 * `appointmentMethodLabel` passam por aqui, os relatórios já mostram a
 * observação sem tratamento próprio.
 */
export function partShortLabel(p: PaymentPart): string {
  if (p.method === 'CARTAO_CREDITO') {
    return p.installments && p.installments > 1
      ? `Crédito ${p.installments}×`
      : 'Crédito à vista';
  }
  if (p.method === 'ANTECIPADO') {
    const note = p.note?.trim();
    return note ? `Antecipado (${note})` : 'Antecipado';
  }
  return PAYMENT_METHOD_SHORT[p.method];
}

/** Composição legível: "Crédito 6× + Pix". Vazio quando não há partes. */
export function compositionLabel(parts: PaymentPart[] | undefined): string {
  if (!parts || parts.length === 0) return '';
  return parts.map(partShortLabel).join(' + ');
}

/**
 * Detalhamento do híbrido pra exibição: cada forma com seu valor separado.
 * Retorna null quando NÃO é híbrido (1 forma ou nenhuma) — aí o rótulo
 * simples já basta. O formato do valor fica a cargo do chamador (recebe
 * `amount` cru) pra não acoplar este util ao formatador de moeda.
 */
export function compositionBreakdown(
  a: Appointment,
): Array<{ label: string; amount: number }> | null {
  const parts = a.paymentParts;
  if (!parts || parts.length < 2) return null;
  return parts.map((p) => ({ label: partShortLabel(p), amount: p.amount }));
}

/** Soma dos valores das partes. */
export function partsTotal(parts: PaymentPart[] | undefined): number {
  return (parts ?? []).reduce((s, p) => s + (Number.isFinite(p.amount) ? p.amount : 0), 0);
}

/**
 * Bucket de agrupamento de um agendamento no relatório de recebimentos.
 * Usa `paymentParts` quando presente; senão cai no texto livre legado.
 */
export function paymentBucketOf(a: Appointment): PaymentBucket {
  const parts = a.paymentParts;
  if (parts && parts.length >= 2) return 'HIBRIDO';
  if (parts && parts.length === 1) return parts[0].method;
  const legacy = parseLegacyMethod(a.paymentMethod);
  return legacy ?? 'SEM_FORMA';
}

/**
 * Rótulo da forma pra célula "Pagamento" do relatório/linha.
 * Composição completa quando há partes; senão o texto legado normalizado.
 */
/**
 * Rótulo do MOMENTO do pagamento pro relatório: "Antecipado (fichário 3,
 * pág. 12)" quando pago antes do dia; null quando pago hoje/pendente.
 * Cobre também o legado em que 'ANTECIPADO' era uma forma.
 */
export function paymentTimingLabel(a: Appointment): string | null {
  const legacy = a.paymentParts?.find((p) => p.method === 'ANTECIPADO');
  if (a.paymentTiming !== 'ANTECIPADO' && !legacy) return null;
  const note = (a.paymentNote ?? legacy?.note ?? '').trim();
  return note ? `Antecipado (${note})` : 'Antecipado';
}

export function appointmentMethodLabel(a: Appointment): string {
  if (a.paymentParts && a.paymentParts.length > 0) return compositionLabel(a.paymentParts);
  const legacy = parseLegacyMethod(a.paymentMethod);
  if (legacy) return legacy === 'CARTAO_CREDITO' ? 'Crédito' : PAYMENT_METHOD_SHORT[legacy];
  return a.paymentMethod?.trim() || '—';
}
